import { useQuery } from '@tanstack/react-query';
import { useAppContext } from 'src/contexts/AppContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { BuyerCreditDto, Feature, RetailClient } from 'src/WebApiController';

/**
 * Query hook to fetch a buyer's credit balance via GET /api/Retail/GetBuyerCredit.
 * Returns null when no credit row exists for the buyer.
 * Gated behind Feature.RetailSale (FF 725).
 *
 * @param targetCurrency - Optional currency to convert the balance into (e.g. sale currency).
 *   When provided and different from the stored credit currency, the backend converts via FX.
 */
export function useGetBuyerCredit(
  buyerEmail: string | null | undefined,
  disabled = false,
  targetCurrency?: string | null
) {
  const { activeAccountWebClientConfig } = useAppContext();
  const hasRetailSale = useUserHasFeature(Feature.RetailSale);

  return useQuery<BuyerCreditDto | null>({
    queryKey: ['getBuyerCredit', buyerEmail, targetCurrency],
    queryFn: async () => {
      if (!buyerEmail) return null;
      return new RetailClient(activeAccountWebClientConfig).getBuyerCredit(
        buyerEmail,
        targetCurrency ?? undefined
      );
    },
    enabled: !disabled && hasRetailSale && !!buyerEmail,
  });
}
